import React, { useState } from "react";
import { CheckCircleIcon, XCircleIcon } from "@heroicons/react/24/solid";
import { MagnifyingGlassPlusIcon } from "@heroicons/react/20/solid";
import EmptyState from "../Shared/EmptyState";
import ImageLightbox from "./ImageLightbox";
import ServiceLocationBadges from "./ServiceLocationBadges";
import ServiceDeliveryBadge from "./ServiceDeliveryBadge";
import { formatPriceWithCurrency } from "../../utility/priceUtils";
import { formatDuration } from "../../utility/bookingUtils";
import {
  deriveCartDeliveryModes,
  getEffectiveDeliveryMode,
} from "../../utility/bookingFlowUtils";

const PublicServiceList = ({
  services,
  loading = false,
  className = "",
  getServiceQuantity = () => 0,
  onServiceQuantityChange = () => {},
  serviceLocationTypes,
  selectedServices = [],
  showLocationBadges = false,
  businessName,
  currency = "CAD",
  primaryColor,
  themeMode,
}) => {
  const [expandedDescriptions, setExpandedDescriptions] = useState(new Set());
  // Holds the service._id whose image is open in the lightbox, or null when
  // closed. Local UI state — not server data — so useState is correct here.
  const [lightboxServiceId, setLightboxServiceId] = useState(null);
  // Holds the service._id that last triggered a conflict, and the conflict
  // message to display. Cleared when the user deselects a conflicting service
  // or the conflict resolves naturally.
  const [conflictState, setConflictState] = useState(null);

  const toggleDescription = (id) =>
    setExpandedDescriptions((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  // Effective (provider-narrowed) delivery mode per service, keyed by id. A
  // hybrid service on a provider that only has fixed availability resolves to
  // "fixed"; a service that isn't bookable in any mode resolves to null.
  const effectiveModeById = {};
  (services || []).forEach((service) => {
    effectiveModeById[service._id] = getEffectiveDeliveryMode(
      service,
      serviceLocationTypes
    );
  });

  // Only badge cards when the page actually MIXES delivery types — otherwise the
  // provider-wide ServiceLocationBadges banner already says it and per-card
  // badges would be redundant noise. null (not bookable) is excluded from the mix.
  const distinctModes = new Set(
    Object.values(effectiveModeById).filter(Boolean)
  );
  const showDeliveryBadges = distinctModes.size >= 2;

  // The list is authoritative as delivered: the server's filterBookableServices
  // already excludes services with no bookable mode before the payload is sent.
  // Re-filtering here on the same cached payload can only DIVERGE from the
  // server — a stale serviceLocationTypes would hide a service that is in fact
  // bookable (e.g. an availability re-enabled server-side, cache not yet
  // refreshed), producing a false "no services" empty state. So trust the
  // server and render what it sent; cache invalidation keeps it fresh.
  const visibleServices = services || [];

  // The service whose image is currently open in the lightbox (null when closed).
  const lightboxService = lightboxServiceId
    ? (services || []).find((s) => s._id === lightboxServiceId)
    : null;

  if (loading) {
    return (
      <div className={`bg-themed-card border-themed rounded-card shadow-lift border px-4 py-6 sm:p-8 ${className}`}>
        <div className="animate-pulse">
          <div className="h-9 bg-themed-card-elevated rounded w-2/3 mb-3"></div>
          <div className="h-4 bg-themed-card-elevated rounded w-1/2 mb-8"></div>
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="border-themed border rounded-card p-5">
                <div className="flex flex-col sm:flex-row sm:gap-5">
                  <div className="w-full aspect-[16/9] sm:w-28 sm:h-28 sm:aspect-auto bg-themed-card-elevated rounded-card mb-4 sm:mb-0"></div>
                  <div className="flex-1 space-y-3">
                    <div className="h-6 bg-themed-card-elevated rounded w-2/3"></div>
                    <div className="h-4 bg-themed-card-elevated rounded w-1/3"></div>
                    <div className="h-4 bg-themed-card-elevated rounded w-full"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (visibleServices.length === 0) {
    return (
      <div className={`bg-themed-card border-themed rounded-card shadow-lift border px-4 py-6 sm:p-8 ${className}`}>
        <EmptyState
          text="No services available"
          subText="This provider hasn't added any services yet. Please check back later."
          buttonText="Contact Provider"
          onClick={() => {}}
        />
      </div>
    );
  }

  return (
    <div className={`bg-themed-card border-themed rounded-card shadow-lift border px-4 py-6 sm:p-8 page-enter ${className}`}>
      {/* Editorial header */}
      <header className="mb-6 reveal-on-enter" style={{ "--reveal-delay": "0ms" }}>
        <h2 className="font-themed-display text-3xl sm:text-4xl text-themed-primary leading-tight">
          Choose your services
        </h2>
        <p className="text-themed-secondary text-sm mt-2">
          Tap to add — you can adjust quantity in your summary.
        </p>
      </header>

      {showLocationBadges && (
        <div className="reveal-on-enter" style={{ "--reveal-delay": "60ms" }}>
          <ServiceLocationBadges
            serviceLocationTypes={serviceLocationTypes}
            businessName={businessName}
            className="mb-6"
          />
        </div>
      )}

      <ul className="space-y-3 sm:space-y-4">
        {visibleServices.map((service, index) => {
          const quantity = getServiceQuantity(service._id);
          const isSelected = quantity > 0;
          const revealDelay = `${120 + index * 60}ms`;

          const handleToggle = (e) => {
            if (e.target.closest("[data-no-toggle]")) return;

            if (isSelected) {
              // Removing a service never causes a conflict.
              setConflictState(null);
              onServiceQuantityChange(service, 0);
              return;
            }

            // Simulate the cart with the service added, then check for conflict.
            // Skip the check when serviceLocationTypes is absent (e.g. in tests
            // or contexts where delivery-type info is unavailable) — matches the
            // original back-compat behavior of allowing any add.
            if (!serviceLocationTypes) {
              setConflictState(null);
              onServiceQuantityChange(service, 1);
              return;
            }
            const prospectiveCart = [
              ...selectedServices,
              { service, quantity: 1 },
            ];
            const { conflict } = deriveCartDeliveryModes(
              prospectiveCart,
              serviceLocationTypes
            );

            if (conflict) {
              // Determine which message to show based on what's already in cart
              // and what the incoming service requires.
              const incomingType = service.deliveryType ?? "hybrid";
              // Find the incompatible type already in the cart
              const hasExistingMobileOnly = selectedServices.some(
                (item) => (item.service.deliveryType ?? "hybrid") === "mobile"
              );
              const hasExistingFixedOnly = selectedServices.some(
                (item) => (item.service.deliveryType ?? "hybrid") === "fixed"
              );

              let message;
              if (incomingType === "fixed" && hasExistingMobileOnly) {
                message =
                  "This service is offered in person only, so it can't be combined with services we travel to you for. Book them separately.";
              } else if (incomingType === "mobile" && hasExistingFixedOnly) {
                message =
                  "This service requires us to travel to you, so it can't be combined with in-person-only services. Book them separately.";
              } else {
                message =
                  "This service can't be combined with your current selection because they require different service types. Book them separately.";
              }

              setConflictState({ serviceId: service._id, message });
              return;
            }

            setConflictState(null);
            onServiceQuantityChange(service, 1);
          };

          const handleKeyDown = (e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              handleToggle(e);
            }
          };

          return (
            <li
              key={service._id}
              className="reveal-on-enter"
              style={{ "--reveal-delay": revealDelay }}
            >
              <div
                role="button"
                tabIndex={0}
                aria-pressed={isSelected}
                onClick={handleToggle}
                onKeyDown={handleKeyDown}
                onMouseEnter={(e) => {
                  if (!isSelected) e.currentTarget.style.borderColor = "var(--theme-primary)";
                }}
                onMouseLeave={(e) => {
                  if (!isSelected) e.currentTarget.style.borderColor = "";
                }}
                className={`relative bg-themed-card border-themed rounded-card p-4 sm:p-5 cursor-pointer transition-colors duration-200 ease-editorial lift-on-hover focus:outline-none focus-themed-ring ${
                  isSelected ? "border-2" : "border"
                }`}
                style={
                  isSelected
                    ? {
                        borderColor: "var(--theme-primary)",
                        backgroundColor: "var(--theme-primary-tint)",
                      }
                    : undefined
                }
              >
                {/* Selected check — top-right */}
                {isSelected && (
                  <CheckCircleIcon
                    aria-hidden="true"
                    className="absolute top-3 right-3 w-6 h-6"
                    style={{ color: "var(--theme-primary)" }}
                  />
                )}

                {/* Mobile-first: banner on top, side-by-side on sm+. Imageless rows skip the thumbnail entirely and let the text column carry the card. */}
                <div className="flex flex-col sm:flex-row sm:gap-5">
                  {service.imageUrl && (
                    <div className="relative w-full aspect-[16/9] sm:w-28 sm:h-28 sm:aspect-auto rounded-card overflow-hidden flex-shrink-0 mb-4 sm:mb-0">
                      <img
                        src={service.imageUrl}
                        alt={service.name}
                        className="w-full h-full object-cover"
                      />
                      {/* Zoom button — opens the full, uncropped image. Uses
                          data-no-toggle + stopPropagation so it never triggers
                          the card's add/remove selection (same mechanism as the
                          "Read more" button below). */}
                      <button
                        type="button"
                        data-no-toggle
                        onClick={(e) => {
                          e.stopPropagation();
                          setLightboxServiceId(service._id);
                        }}
                        aria-label={`View larger image of ${service.name}`}
                        className="absolute bottom-1.5 right-1.5 rounded-full p-1.5 bg-ink-900/70 backdrop-blur-sm ring-1 ring-white/60 shadow-hairline hover:bg-ink-900/85 focus:outline-none focus:ring-2 focus:ring-white/80"
                      >
                        <MagnifyingGlassPlusIcon
                          aria-hidden="true"
                          className="h-5 w-5 text-white"
                        />
                      </button>
                    </div>
                  )}

                  {/* Text column */}
                  <div className="flex-1 min-w-0 sm:pr-10">
                    {/* Name + desktop price row */}
                    <div className="flex items-start gap-4">
                      <h3 className="font-themed-display text-xl sm:text-2xl text-themed-primary leading-tight flex-1">
                        {service.name}
                      </h3>
                      {/* Price (desktop only — right-aligned next to title) */}
                      <p className="hidden sm:block font-themed-display text-xl text-themed-primary tabular shrink-0 leading-tight">
                        {formatPriceWithCurrency(service.price, currency)}
                      </p>
                    </div>

                    {/* Duration chip — under the name on all viewports */}
                    <div className="mt-2 flex items-center gap-3 flex-wrap">
                      <span className="smallcaps text-xs text-themed-secondary tabular">
                        {formatDuration(service.duration)}
                      </span>
                      {/* Per-service delivery indicator — only when the page
                          mixes delivery types; reflects provider-narrowed
                          effective capability (null modes render nothing). */}
                      {showDeliveryBadges && (
                        <ServiceDeliveryBadge
                          mode={effectiveModeById[service._id]}
                        />
                      )}
                      {/* Mobile price — own line, prominent */}
                      <span className="sm:hidden font-themed-display text-2xl text-themed-primary tabular ml-auto leading-none">
                        {formatPriceWithCurrency(service.price, currency)}
                      </span>
                    </div>

                    {/* Description with Read more */}
                    {service.description && (
                      <div className="mt-3">
                        <p
                          className={`text-sm text-themed-secondary leading-relaxed whitespace-pre-line ${
                            service.description.length > 120 &&
                            !expandedDescriptions.has(service._id)
                              ? "line-clamp-2"
                              : ""
                          }`}
                        >
                          {service.description}
                        </p>
                        {service.description.length > 120 && (
                          <button
                            type="button"
                            data-no-toggle
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleDescription(service._id);
                            }}
                            className="text-xs font-medium mt-1 underline hover:opacity-80 transition-opacity"
                            style={{ color: "var(--theme-primary)" }}
                          >
                            {expandedDescriptions.has(service._id)
                              ? "Show less"
                              : "Read more"}
                          </button>
                        )}
                      </div>
                    )}

                    {/* Cart-conflict inline message — shown when adding this
                        service would create an incompatible cart (e.g. mixing
                        mobile-only and fixed-only services). The service is
                        NOT added; the user must book them separately. */}
                    {conflictState?.serviceId === service._id && (
                      <div
                        className="mt-3 flex items-start gap-2 rounded-card bg-signal-red-50 border border-signal-red-100 px-3 py-2"
                        role="alert"
                      >
                        <XCircleIcon
                          className="mt-0.5 h-4 w-4 flex-shrink-0 text-signal-red-600"
                          aria-hidden="true"
                        />
                        <p className="text-sm text-signal-red-700 leading-snug">
                          {conflictState.message}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </li>
          );
        })}
      </ul>

      {/* Single lightbox instance, driven by the id of the open service. */}
      <ImageLightbox
        open={!!lightboxServiceId}
        onClose={() => setLightboxServiceId(null)}
        imageUrl={lightboxService?.imageUrl}
        alt={lightboxService?.name}
        primaryColor={primaryColor}
        themeMode={themeMode}
      />
    </div>
  );
};

export default PublicServiceList;
